import { PERM, type PermissionAction } from '@/shared/auth/types'
import type { IconName } from './icons'
import { MODULES } from './modules'

/**
 * The command palette's contents and its ranking.
 *
 * <h2>Why this file has no React in it</h2>
 * Ranking is the part that decides whether the palette feels sharp or stupid,
 * and it is the part worth testing. Keeping it as plain functions lets the
 * tests state the behaviour directly -- "pen" must reach Persetujuan before
 * Pengaturan -- without rendering anything.
 *
 * <h2>Where the destinations come from</h2>
 * From each module's menu in {@link MODULES}, not a second hand-maintained list. A palette that has to
 * be updated alongside the menu is one that will quietly fall a release behind
 * it, and the failure is invisible: the page is simply unreachable by search,
 * and nobody files a ticket about a result they never saw.
 */

export interface Command {
  id: string
  label: string
  /** Shown greyed at the end of the row, so two similar labels stay distinct. */
  group: string
  icon: IconName
  /** Words people actually type that do not appear in the label. */
  keywords?: string[]
  /** Internal route. */
  to?: string
  /** Another application; opens in a new tab. */
  href?: string
  /** Anything that is not navigation, run by the palette's host. */
  run?: 'logout'
}

type CanFn = (permCode: string, action?: PermissionAction) => boolean

/**
 * The vocabulary the menu labels do not carry.
 *
 * The people using this say "approval" out of habit and read "Persetujuan" on
 * screen; both have to find the same page. The English terms are here because
 * they are what gets typed, not because the interface is bilingual.
 */
const KEYWORDS: Record<string, string[]> = {
  '/': ['beranda', 'home', 'portal', 'awal'],
  '/billing': ['ringkasan', 'dashboard', 'billing', 'summary'],
  '/billing/proforma': ['proforma', 'draft', 'dokumen', 'tagihan', 'pf'],
  '/billing/customers': ['customer', 'klien', 'npwp', 'master', 'nitku'],
  '/billing/approvals': ['approval', 'approve', 'persetujuan', 'inbox', 'kotak masuk', 'menunggu'],
  '/billing/invoices': ['invoice', 'faktur', 'tagihan', 'ar'],
  '/billing/reconciliation': ['rekonsiliasi', 'recon', 'selisih', 'nav', 'oracle', 'cocok'],
  '/billing/settings/approval-matrix': ['matriks', 'matrix', 'jenjang', 'level', 'alur', 'workflow'],
  '/billing/settings/permissions': ['hak akses', 'permission', 'role', 'peran', 'izin'],
  '/billing/settings/audit': ['audit', 'trail', 'log', 'aktivitas', 'jejak', 'riwayat'],
  '/billing/notifications': ['notifikasi', 'notification', 'pemberitahuan', 'lonceng', 'kabar'],
  '/billing/account': ['profil', 'akun', 'preferensi', 'setelan', 'profile'],
  '/piutang': ['piutang', 'receivable', 'ar', 'aging', 'umur', 'penagihan', 'konfirmasi', 'ekspedisi', 'resi', 'jatuh tempo'],
  '/piutang/reports': ['laporan', 'report', 'listing', 'aging', 'excel'],
}

/**
 * Builds the palette for one user.
 *
 * Everything is filtered through the same permission check the menu uses. A
 * palette that lists pages the server will refuse is not a shortcut; it is a
 * directory of other people's work.
 */
export function buildCommands(can: CanFn, options: { mintUrl?: string } = {}): Command[] {
  // Beranda is not in any module's menu -- it lives in the top bar -- so it is
  // added here by hand, first, as the one place every palette can reach.
  const commands: Command[] = [
    { id: 'nav:/', label: 'Beranda', group: 'Navigasi', icon: 'home', to: '/', keywords: KEYWORDS['/'] },
  ]

  for (const module of MODULES) {
    if (module.kind !== 'internal' || !can(module.permCode, module.action ?? 'view')) continue
    for (const section of module.nav) {
      for (const item of section.items) {
        if (!item.to) continue
        if (item.permCode && !can(item.permCode, item.action ?? 'view')) continue
        const keywords = KEYWORDS[item.to]
        commands.push({
          id: `nav:${item.to}`,
          label: item.label,
          group: section.title ?? module.short,
          icon: item.icon,
          to: item.to,
          ...(keywords ? { keywords } : {}),
        })
      }
    }
  }

  // Actions come after the destinations: the palette is first of all a way to
  // reach a page, and a verb that changes something should never be what the
  // first keystroke lands on.
  if (can(PERM.BILLING_PROFORMA, 'add')) {
    commands.push({
      id: 'action:new-proforma',
      label: 'Buat Proforma Baru',
      group: 'Tindakan',
      icon: 'plus',
      keywords: ['baru', 'new', 'tambah', 'create'],
      to: '/billing/proforma/new',
    })
  }

  commands.push({
    id: 'action:change-password',
    label: 'Ubah Kata Sandi',
    group: 'Tindakan',
    icon: 'user',
    keywords: ['password', 'sandi', 'ganti', 'keamanan'],
    to: '/billing/account/change-password',
  })

  if (can(PERM.PORTAL_MONITORING) && options.mintUrl) {
    commands.push({
      id: 'action:monitoring',
      label: 'Buka SIPAY Monitoring',
      group: 'Aplikasi lain',
      icon: 'external',
      keywords: ['mint', 'monitoring', 'transaksi', 'host'],
      href: options.mintUrl,
    })
  }

  commands.push({
    id: 'action:logout',
    label: 'Keluar',
    group: 'Tindakan',
    icon: 'logout',
    keywords: ['logout', 'sign out', 'akhiri sesi'],
    run: 'logout',
  })

  return commands
}

/** Returned by {@link scoreCommand} when the query does not match at all. */
export const NO_MATCH = -1

function normalise(value: string): string {
  return value.toLowerCase().replace(/\s+/g, ' ').trim()
}

/** Every character of `needle`, in order, somewhere in `haystack`. */
function isSubsequence(needle: string, haystack: string): boolean {
  if (needle.length === 0) return true
  let at = 0
  for (const character of haystack) {
    if (character === needle[at]) at += 1
    if (at === needle.length) return true
  }
  return false
}

/**
 * How well one command answers one query.
 *
 * The bands are deliberately coarse and ordered by how confident the match is,
 * rather than being a similarity score: with a dozen commands the useful
 * question is not "how close" but "did they mean the start of a word, a word
 * buried in the middle, or are we down to guessing".
 */
export function scoreCommand(command: Command, query: string): number {
  const q = normalise(query)
  if (!q) return 0

  const label = normalise(command.label)
  if (label.startsWith(q)) return 100
  // A word start, so "sandi" finds "Ubah Kata Sandi": people search for the
  // distinctive word, which is rarely the first one.
  if (label.split(' ').some((word) => word.startsWith(q))) return 80
  if (label.includes(q)) return 60

  const keywords = (command.keywords ?? []).map(normalise)
  if (keywords.some((keyword) => keyword.startsWith(q))) return 50
  if (keywords.some((keyword) => keyword.includes(q))) return 40

  if (normalise(command.group).startsWith(q)) return 30

  // Last resort, and only against the label: subsequence matching over the
  // keywords too would make almost everything match almost everything.
  if (isSubsequence(q, label)) return 20

  return NO_MATCH
}

/**
 * Filters and orders the palette for a query.
 *
 * An empty query returns everything in menu order rather than a list of recent
 * destinations. The whole set fits on screen, and a list that is always the
 * same is one people learn by position; recents reorder themselves under the
 * cursor, which is how you end up opening the wrong row.
 */
export function rankCommands(commands: Command[], query: string): Command[] {
  if (!query.trim()) return commands

  return commands
    .map((command, index) => ({ command, index, score: scoreCommand(command, query) }))
    .filter((entry) => entry.score !== NO_MATCH)
    .sort(
      (a, b) =>
        b.score - a.score ||
        // Shorter label first at equal confidence: it is the more general page.
        a.command.label.length - b.command.label.length ||
        a.index - b.index,
    )
    .map((entry) => entry.command)
}

/** The proforma list, filtered by free text (GET /proformas?q=). */
function searchLink(text: string): { label: string; to: string } {
  return { label: `Cari proforma “${text}”`, to: `/billing/proforma?q=${encodeURIComponent(text)}` }
}

/**
 * A typed document reference, turned into the first row of the palette.
 *
 * A bare number is an internal id and opens that document. Anything with a
 * slash is a document number -- "PF/01/2609/00131", or the tail of one -- and
 * opens the list filtered to it: the list endpoint matches it anywhere in the
 * number, so a partial number still finds its document.
 */
export function documentJump(query: string): { label: string; to: string } | null {
  const text = query.trim()
  const match = /^#?(\d{1,9})$/.exec(text)
  const digits = match?.[1]
  if (digits) {
    const id = String(Number(digits))
    return { label: `Buka proforma #${id}`, to: `/billing/proforma/${id}` }
  }
  if (text.includes('/')) return searchLink(text)
  return null
}

/**
 * The last-resort row: search the proforma list for whatever was typed, which
 * is how a customer name is found. Last, not first -- "pro" is far more often
 * someone on their way to the Proforma page than a customer called "Pro".
 */
export function documentSearch(query: string): { label: string; to: string } | null {
  const text = query.trim()
  if (text.length < 3 || documentJump(text)) return null
  return searchLink(text)
}
